import React from 'react';
import { formatCustomDateTime } from '../utils/dateUtils';

interface CustomFieldValue {
  custom_field_id: number;
  custom_field_value?: any;
  custom_field?: {
    custom_field_id: number;
    custom_field_display_name: string;
    custom_field_data_type: {
      custom_field_data_type_key: string;
      custom_field_data_type_display_name: string;
    };
  };
}

interface CustomFieldDisplayProps {
  customFields?: CustomFieldValue[];
  showTitle?: boolean;
}

const CustomFieldDisplay: React.FC<CustomFieldDisplayProps> = ({ 
  customFields = [], 
  showTitle = true 
}) => {
  if (!customFields || customFields.length === 0) {
    return null;
  }

  const parseIfString = (val: any) => {
    if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  };

  const formatValue = (field: CustomFieldValue) => {
    let value: any;
    
    if (field.custom_field_value && typeof field.custom_field_value === 'object') {
      if ('custom_field_value' in field.custom_field_value) {
        value = field.custom_field_value.custom_field_value;
      } else if ('value' in field.custom_field_value) {
        value = field.custom_field_value.value;
      } else {
        value = field.custom_field_value;
      }
    } else {
      value = field.custom_field_value;
    }

    const dataType = (
      field.custom_field?.custom_field_data_type?.custom_field_data_type_key ||
      (field as any).custom_field_data_type?.custom_field_data_type_key
    )?.toUpperCase();
    
    if (value === null || value === undefined || value === '') {
      if (dataType === 'SWITCH' || dataType === 'BOOLEAN' || dataType === 'CHECKBOX') {
        value = false;
      } else {
        return 'Not specified';
      }
    }

    value = parseIfString(value);

    switch (dataType) {
      case 'DATE':
        try {
          const formattedDate = formatCustomDateTime(value, true);
          if (formattedDate === 'Invalid date' || !formattedDate) {
            return 'Not specified';
          }
          return formattedDate;
        } catch {
          return 'Not specified';
        }

      case 'TIME':
        return typeof value === 'string' ? value : 'Not specified';

      case 'DATETIME':
        try {
          const formattedDateTime = formatCustomDateTime(value);
          if (formattedDateTime === 'Invalid date' || !formattedDateTime) {
            return 'Not specified';
          }
          return formattedDateTime;
        } catch {
          return 'Not specified';
        }
      
      case 'SWITCH':
        const switchValue = value === true || value === 'true' || value === 'TRUE' || value === 1 || value === '1';
        return (
          <div className="form-check form-switch d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={switchValue}
              disabled
              readOnly
              style={{ cursor: 'not-allowed', width: '30px', height: '15px' }}
            />
          </div>
        );

      case 'CHECKBOX':
      case 'BOOLEAN':
        return value ? 'Yes' : 'No';
      
      case 'DROPDOWN':
      case 'SINGLE_OPTION':
      case 'RADIO_OPTION':
        if (typeof value === 'object' && value !== null) {
          if (Object.keys(value).length === 0) {
            return 'Not specified';
          }
          return value.custom_field_option_display_label || value.choice_label || value.label || value.value || 'Not specified';
        }
        return typeof value === 'string' || typeof value === 'number' ? String(value) : 'Not specified';

      case 'MULTIPLE_OPTION':
        if (Array.isArray(value)) {
          const labels = value
            .map((v: any) => {
              if (typeof v === 'object' && v !== null) {
                return v.custom_field_option_display_label || v.choice_label || v.label || v.value || String(v);
              }
              return String(v);
            })
            .filter(Boolean);
          return labels.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {labels.map((label, idx) => (
                <span key={idx} className="badge bg-primary-transparent text-primary">
                  {label}
                </span>
              ))}
            </div>
          ) : 'Not specified';
        }
        if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) {
          return 'Not specified';
        }
        return typeof value === 'string' ? value : 'Not specified';
      
      case 'DEPENDENT_FIELD':
        if (Array.isArray(value)) {
          const selectedLabels = value
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map((level: any) => {
              const selectedOption = level.options?.find((opt: any) => opt.is_selected);
              return selectedOption?.option_label || selectedOption?.label;
            })
            .filter(Boolean);
          
          return selectedLabels.length > 0 ? (
            <div className="d-flex align-items-center gap-2">
              {selectedLabels.map((label, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <i className="ti ti-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>}
                  <span className="badge bg-info-transparent text-info">{label}</span>
                </React.Fragment>
              ))}
            </div>
          ) : 'Not specified';
        }
        if (typeof value === 'object' && value !== null) {
          if (Object.keys(value).length === 0) {
            return 'Not specified';
          }
          const parts: string[] = [];
          Object.keys(value).forEach((key) => {
            const levelValue = value[key];
            if (levelValue && typeof levelValue === 'object') {
              parts.push(levelValue.custom_field_option_display_label || levelValue.label || levelValue.value || '');
            } else if (levelValue) {
              parts.push(String(levelValue));
            }
          });
          return parts.filter(Boolean).join(' > ') || 'Not specified';
        }
        return 'Not specified';
      
      case 'INTEGER':
      case 'INTEGER_VALUES':
      case 'INTEGER_SMALL':
      case 'INTEGER_LARGE':
      case 'SMALL_INTEGER':
      case 'SMALL_INTEGER_VALUES':
      case 'SMALLINT':
      case 'LARGE_INTEGER':
      case 'LARGE_INTEGER_VALUES':
      case 'BIGINT':
      case 'NUMBER':
        if (typeof value === 'number') {
          return value.toString();
        }
        if (typeof value === 'string' && !isNaN(Number(value))) {
          return Number(value).toString();
        }
        return 'Not specified';
      
      case 'DECIMAL':
      case 'DECIMAL_VALUE':
      case 'DECIMAL_VALUES':
      case 'FLOAT':
      case 'FLOAT_VALUES':
      case 'NUMERIC':
      case 'DOUBLE':
      case 'DOUBLE_VALUES':
        if (typeof value === 'number') {
          return value.toFixed(5);
        }
        if (typeof value === 'string' && !isNaN(Number(value))) {
          return Number(value).toFixed(5);
        }
        return 'Not specified';
      
      case 'CURRENCY':
        if (typeof value === 'object' && value !== null) {
          if (Object.keys(value).length === 0) {
            return 'Not specified';
          }
          const amount = value.amount || value.value || 0;
          const currency = value.currency || value.currency_code || '';
          return `${currency} ${Number(amount).toFixed(2)}`;
        }
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
          return Number(value).toFixed(2);
        }
        return 'Not specified';
      
      case 'DATE_RANGE':
        if (typeof value === 'object' && value !== null) {
          const startDate = value.start_date || value.startDate;
          const endDate = value.end_date || value.endDate;
          if (startDate && endDate) {
            try {
              return (
                <div className="d-flex align-items-center gap-1">
                  <span className="badge bg-success-transparent text-success">
                    {formatCustomDateTime(startDate, true)}
                  </span>
                  <i className="fa-solid fa-arrow-right text-muted" style={{ fontSize: '0.6rem' }}></i>
                  <span className="badge bg-success-transparent text-success">
                    {formatCustomDateTime(endDate, true)}
                  </span>
                </div>
              );
            } catch {
              return `${startDate} - ${endDate}`;
            }
          }
        }
        return 'Not specified';
      
      case 'TIME_RANGE':
        if (typeof value === 'object' && value !== null) {
          const startTime = value.start_time || value.startTime;
          const endTime = value.end_time || value.endTime;
          if (startTime && endTime) {
            return (
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-warning-transparent text-warning">
                  {startTime}
                </span>
                <i className="fa-solid fa-arrow-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                <span className="badge bg-warning-transparent text-warning">
                  {endTime}
                </span>
              </div>
            );
          }
        }
        return 'Not specified';
      
      case 'URL':
        if (value && typeof value === 'string') {
          return (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-underline">
              {value}
            </a>
          );
        }
        return 'Not specified';
      
      case 'EMAIL':
        if (value && typeof value === 'string') {
          return (
            <a href={`mailto:${value}`} className="text-primary text-decoration-underline">
              {value}
            </a>
          );
        }
        return 'Not specified';
      
      case 'PHONE':
        if (value && typeof value === 'string') {
          return (
            <a href={`tel:${value}`} className="text-primary text-decoration-underline">
              {value}
            </a>
          );
        }
        return 'Not specified';
      
      case 'TEXT':
      case 'TEXT_SMALL':
      case 'TEXT_LARGE':
      case 'TEXTAREA':
        if (typeof value === 'string') {
          return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {value}
            </div>
          );
        }
        if (typeof value === 'object' && value !== null) {
          if (Object.keys(value).length === 0) {
            return 'Not specified';
          }
          return JSON.stringify(value);
        }
        return typeof value === 'number' ? String(value) : 'Not specified';
      
      default:
        
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return 'Not specified';
          }
          if (value.every((v: any) => typeof v === 'string')) {
            return (
              <div className="d-flex flex-wrap gap-2">
                {value.map((label, idx) => (
                  <span key={idx} className="badge bg-primary-transparent text-primary">
                    {label}
                  </span>
                ))}
              </div>
            );
          }
          
          if (value.length > 0 && value[0].level_name && value[0].options) {
            const selectedLabels = value
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((level: any) => {
                const selectedOption = level.options?.find((opt: any) => opt.is_selected);
                return selectedOption?.option_label || selectedOption?.label;
              })
              .filter(Boolean);
            
            return selectedLabels.length > 0 ? (
              <div className="d-flex align-items-center gap-2">
                {selectedLabels.map((label, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <i className="ti ti-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>}
                    <span className="badge bg-info-transparent text-info">{label}</span>
                  </React.Fragment>
                ))}
              </div>
            ) : 'Not specified';
          }
        }
        
        if (typeof value === 'object' && value !== null) {
          if (Object.keys(value).length === 0) {
            return 'Not specified';
          }
          
          if (value.start_date && value.end_date) {
            try {
              return (
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-success-transparent text-success">
                    {formatCustomDateTime(value.start_date, true)}
                  </span>
                  <i className="ti ti-arrow-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                  <span className="badge bg-success-transparent text-success">
                    {formatCustomDateTime(value.end_date, true)}
                  </span>
                </div>
              );
            } catch {
              return `${value.start_date} - ${value.end_date}`;
            }
          }
          
          if (value.start_time && value.end_time) {
            return (
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-warning-transparent text-warning">
                  {value.start_time}
                </span>
                <i className="ti ti-arrow-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                <span className="badge bg-warning-transparent text-warning">
                  {value.end_time}
                </span>
              </div>
            );
          }
          
          return 'Not specified';
        }
        
        return typeof value === 'string' || typeof value === 'number' ? String(value) : 'Not specified';
    }
  };

  const getFieldName = (field: CustomFieldValue): string => {
    if (field.custom_field?.custom_field_display_name) {
      return field.custom_field.custom_field_display_name;
    }
    
    if ((field as any).custom_field_display_name) {
      return (field as any).custom_field_display_name;
    }
    
    if ((field as any).display_name) {
      return (field as any).display_name;
    }
    
    if ((field as any).name) {
      return (field as any).name;
    }
    
    return 'Unknown Field';
  };

  const getFieldSpacing = (field: CustomFieldValue): { labelMargin: string; valueMargin: string; valueGap: string } => {
    const dataType = (
      field.custom_field?.custom_field_data_type?.custom_field_data_type_key ||
      (field as any).custom_field_data_type?.custom_field_data_type_key
    )?.toUpperCase();

    switch (dataType) {
      case 'DATE_RANGE':
        return { labelMargin: 'mb-2', valueMargin: 'ms-0', valueGap: 'gap-2' };
      case 'TIME_RANGE':
      case 'MULTIPLE_OPTION':
      case 'DEPENDENT_FIELD':
        return { labelMargin: 'mb-2', valueMargin: 'ms-3', valueGap: 'gap-2' };
      
      case 'SWITCH':
        return { labelMargin: 'mb-1', valueMargin: 'ms-2', valueGap: 'gap-4' };
      
      case 'TEXT':
      case 'TEXT_LARGE':
      case 'TEXTAREA':
        return { labelMargin: 'mb-2', valueMargin: 'ms-3', valueGap: 'gap-2' };
      
      case 'URL':
      case 'EMAIL':
      case 'PHONE':
        return { labelMargin: 'mb-1', valueMargin: 'ms-3', valueGap: 'gap-2' };
      
      default:
        return { labelMargin: 'mb-1', valueMargin: 'ms-3', valueGap: 'gap-2' };
    }
  };

  const fieldsWithValues = customFields.filter(field => {
    const value = formatValue(field);
    return value !== 'Not specified';
  });

  if (fieldsWithValues.length === 0) {
    return null;
  }

  return (
    <>
      {showTitle && (
        <>
          <hr />
          <h6 className="mb-3 fw-semibold">
            <i className="ti ti-forms me-2" />
            Custom Fields
          </h6>
        </>
      )}
      <ul className="list-unstyled">
        {fieldsWithValues.map((field, index) => {
          const spacing = getFieldSpacing(field);
          return (
            <li key={field.custom_field_id || index} className="mb-3">
              <div className={`text-muted ${spacing.labelMargin}`}>
                {getFieldName(field)}
              </div>
              <div className={`d-flex align-items-center ${spacing.valueGap}`}>
                <i className="fa-solid fa-arrow-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                <span className="text-break">
                  {formatValue(field)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default CustomFieldDisplay;

